import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  ExternalLink,
  Library,
  Loader2,
  Plus,
} from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import type { CoachingHomeworkWithProblem } from '../problems/types'
import type { SourceContent, SourceContentItem } from '../sources/types'
import {
  type SourceWithContent,
  useSourcesWithContent,
} from './hooks/use-sources-with-content'

interface SourceContentWidgetProps {
  assignedHomework: CoachingHomeworkWithProblem[]
  onAssignSourceContent: (
    sourceContentId: string,
    title: string,
    url?: string,
  ) => Promise<void>
  onAssignSourceContentItem?: (
    item: SourceContentItem,
    chapterTitle: string,
  ) => Promise<void>
}

// Content type labels for display
const CONTENT_TYPE_LABELS: Record<string, string> = {
  chapter: 'Chapter',
  module: 'Module',
  playlist: 'Playlist',
  series: 'Series',
  section: 'Section',
  lesson: 'Lesson',
}

// Item type labels for display
const ITEM_TYPE_LABELS: Record<string, string> = {
  problem: 'Problem',
  coding_exercise: 'Exercise',
  system_design: 'System Design',
  task: 'Task',
  video: 'Video',
  article: 'Article',
  PROBLEM: 'Problem',
  CODING_EXERCISE: 'Exercise',
  SYSTEM_DESIGN: 'System Design',
  TASK: 'Task',
  VIDEO: 'Video',
  ARTICLE: 'Article',
  RESOURCE: 'Resource',
}

export function SourceContentWidget({
  assignedHomework,
  onAssignSourceContent,
  onAssignSourceContentItem,
}: SourceContentWidgetProps) {
  const { sourcesWithContent, loading, error, fetchChapterItems } =
    useSourcesWithContent()
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [addingContentId, setAddingContentId] = useState<string | null>(null)

  // Drill-down state
  const [viewLevel, setViewLevel] = useState<'chapters' | 'items'>('chapters')
  const [selectedChapter, setSelectedChapter] = useState<SourceContent | null>(
    null,
  )
  const [chapterItems, setChapterItems] = useState<SourceContentItem[]>([])
  const [loadingItems, setLoadingItems] = useState(false)
  const [addingItemId, setAddingItemId] = useState<string | null>(null)
  // Guards against out-of-order responses: rapid chapter A->B clicks could
  // otherwise let A's slower fetch resolve after B's and overwrite B's items
  // while the breadcrumb still shows B.
  const drillRequestRef = useRef(0)

  // Get set of already assigned source content IDs
  const assignedContentIds = useMemo(() => {
    return new Set(
      assignedHomework
        .filter((h) => h.status === 'active' && h.sourceContentId)
        .map((h) => h.sourceContentId as string),
    )
  }, [assignedHomework])

  // Get sets of already assigned item IDs by type
  const assignedItemIds = useMemo(() => {
    const problemIds = new Set<string>()
    const exerciseIds = new Set<string>()
    const systemDesignIds = new Set<string>()
    const taskIds = new Set<string>()
    // RESOURCE items are assigned by creating a TASK homework row whose
    // Task.resourceId points back at the originating resource — track that
    // so a resource already assigned via a task shows "Added" too.
    const resourceIds = new Set<string>()

    for (const h of assignedHomework) {
      if (h.status !== 'active') continue
      if (h.problemId) problemIds.add(h.problemId)
      if (h.exerciseId) exerciseIds.add(h.exerciseId)
      if (h.systemDesignProblemId) systemDesignIds.add(h.systemDesignProblemId)
      if (h.taskId) taskIds.add(h.taskId)
      if (h.task?.resourceId) resourceIds.add(h.task.resourceId)
    }

    return { problemIds, exerciseIds, systemDesignIds, taskIds, resourceIds }
  }, [assignedHomework])

  // Get the currently selected source (or first one if none selected)
  const selectedSource = useMemo(() => {
    if (sourcesWithContent.length === 0) return null
    if (selectedSourceId) {
      return (
        sourcesWithContent.find((s) => s.id === selectedSourceId) ||
        sourcesWithContent[0]
      )
    }
    return sourcesWithContent[0]
  }, [sourcesWithContent, selectedSourceId])

  // Calculate stats for each source
  const sourceStats = useMemo(() => {
    const stats = new Map<
      string,
      { total: number; assigned: number; unassigned: number }
    >()
    for (const source of sourcesWithContent) {
      const total = source.contents.length
      const assigned = source.contents.filter((c) =>
        assignedContentIds.has(c.id),
      ).length
      stats.set(source.id, {
        total,
        assigned,
        unassigned: total - assigned,
      })
    }
    return stats
  }, [sourcesWithContent, assignedContentIds])

  const handleAddContent = async (content: SourceContent) => {
    if (assignedContentIds.has(content.id)) return

    setAddingContentId(content.id)
    try {
      // Format title as "Review Chapter XX: Name" or "Review Module XX: Name"
      const typeLabel = CONTENT_TYPE_LABELS[content.kind] || content.kind
      const orderPart = content.order > 0 ? ` ${content.order}` : ''
      const formattedTitle = `Review ${typeLabel}${orderPart}: ${content.title}`

      await onAssignSourceContent(
        content.id,
        formattedTitle,
        content.url ?? undefined,
      )
      toast.success(`Added "${content.title}"`)
    } catch (err) {
      console.error('Error adding content:', err)
      toast.error('Failed to add content')
    } finally {
      setAddingContentId(null)
    }
  }

  // Handle adding an item as homework
  const handleAddItem = async (item: SourceContentItem) => {
    if (!onAssignSourceContentItem || !selectedChapter) return

    // Check if already assigned based on item type
    const isAssigned = isItemAssigned(item)
    if (isAssigned) return

    setAddingItemId(item.id)
    try {
      await onAssignSourceContentItem(item, selectedChapter.title)
      toast.success(`Added "${getItemTitle(item)}"`)
    } catch (err) {
      console.error('Error adding item:', err)
      toast.error('Failed to add item')
    } finally {
      setAddingItemId(null)
    }
  }

  // Check if an item is already assigned
  const isItemAssigned = (item: SourceContentItem): boolean => {
    switch (item.contentType) {
      case 'PROBLEM':
        return item.contentId
          ? assignedItemIds.problemIds.has(item.contentId)
          : false
      case 'CODING_EXERCISE':
        return item.contentId
          ? assignedItemIds.exerciseIds.has(item.contentId)
          : false
      case 'SYSTEM_DESIGN':
        return item.contentId
          ? assignedItemIds.systemDesignIds.has(item.contentId)
          : false
      case 'TASK':
        return item.contentId
          ? assignedItemIds.taskIds.has(item.contentId)
          : false
      case 'RESOURCE':
        return item.contentId
          ? assignedItemIds.resourceIds.has(item.contentId)
          : false
      // VIDEO and ARTICLE are external - check by URL in title field
      // For now, assume not assigned (could track by title/url later)
      default:
        return false
    }
  }

  // Get display title for an item
  const getItemTitle = (item: SourceContentItem): string => {
    // For internal content types, prefer the populated data
    if (item.problem) return item.problem.title
    if (item.codingExercise) return item.codingExercise.title
    if (item.systemDesignProblem) return item.systemDesignProblem.title
    if (item.task) return item.task.title
    // For external types (VIDEO, ARTICLE), use the title field
    return item.title || 'Untitled'
  }

  // Abandon any in-flight chapter fetch. Bumping the counter makes every
  // pending response fail its `requestId` check, so navigating away (back to
  // chapters, switching source tab, collapsing the widget) can no longer be
  // undone by a fetch that resolves afterwards.
  const invalidateDrill = useCallback(() => {
    drillRequestRef.current++
    setLoadingItems(false)
  }, [])

  // Handle drilling into a chapter to see its items
  const handleDrillIntoChapter = useCallback(
    async (chapter: SourceContent) => {
      const requestId = ++drillRequestRef.current
      setSelectedChapter(chapter)
      setLoadingItems(true)
      try {
        const items = await fetchChapterItems(chapter.id)
        if (requestId !== drillRequestRef.current) return
        setChapterItems(items)
        setViewLevel('items')
      } catch (err) {
        if (requestId !== drillRequestRef.current) return
        console.error('Error fetching chapter items:', err)
        toast.error('Failed to load chapter items')
      } finally {
        if (requestId === drillRequestRef.current) setLoadingItems(false)
      }
    },
    [fetchChapterItems],
  )

  // Handle navigating back to chapters view
  const handleBackToChapters = useCallback(() => {
    invalidateDrill()
    setViewLevel('chapters')
    setSelectedChapter(null)
    setChapterItems([])
  }, [invalidateDrill])

  // Build content type label with order number (e.g., "Chapter 5", "Module 3")
  const getContentLabel = (content: SourceContent) => {
    const typeLabel = CONTENT_TYPE_LABELS[content.kind] || content.kind
    if (content.order > 0) {
      return `${typeLabel} ${content.order}`
    }
    return typeLabel
  }

  if (loading) {
    return (
      <div className="bg-card border-border overflow-hidden rounded-lg border">
        <div className="flex items-center gap-2 p-4">
          <Loader2 size={18} className="text-muted-foreground animate-spin" />
          <span className="text-muted-foreground text-sm">
            Loading reading materials...
          </span>
        </div>
      </div>
    )
  }

  if (error) {
    return null // Silently hide if there's an error
  }

  if (sourcesWithContent.length === 0) {
    return null // Hide if no sources have content
  }

  return (
    <div className="bg-card border-border overflow-hidden rounded-lg border">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="border-border hover:bg-muted/50 flex w-full items-center justify-between border-b p-4 transition-colors"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-muted-foreground" />
          <h2 className="text-foreground text-lg font-semibold">
            Source Review Tasks
          </h2>
          <span className="bg-muted text-muted-foreground ml-2 rounded px-2 py-0.5 text-xs">
            {sourcesWithContent.length} source
            {sourcesWithContent.length !== 1 ? 's' : ''}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-muted-foreground" />
        ) : (
          <ChevronDown size={20} className="text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4">
          {/* Source tabs */}
          {sourcesWithContent.length > 1 && viewLevel === 'chapters' && (
            <div className="mb-4 flex flex-wrap gap-2">
              {sourcesWithContent.map((source) => {
                const stats = sourceStats.get(source.id)
                const isSelected = selectedSource?.id === source.id

                return (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => {
                      invalidateDrill()
                      setSelectedSourceId(source.id)
                    }}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    {source.icon && (
                      <AuthoredIcon
                        icon={source.icon}
                        size="sm"
                        className="text-muted-foreground shrink-0"
                        fallback={Library}
                      />
                    )}
                    <span className="font-medium">{source.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ({stats?.unassigned || 0}/{stats?.total || 0})
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Breadcrumb navigation when viewing items */}
          {viewLevel === 'items' && selectedSource && selectedChapter && (
            <Breadcrumb
              sourceName={selectedSource.name}
              sourceIcon={selectedSource.icon}
              chapterTitle={selectedChapter.title}
              chapterType={selectedChapter.kind}
              chapterOrder={selectedChapter.order}
              onBackToChapters={handleBackToChapters}
            />
          )}

          {/* Content list for selected source (chapters view) */}
          {viewLevel === 'chapters' && selectedSource && (
            <SourceContentList
              source={selectedSource}
              assignedContentIds={assignedContentIds}
              addingContentId={addingContentId}
              onAddContent={handleAddContent}
              onDrillInto={handleDrillIntoChapter}
              getContentLabel={getContentLabel}
              hasItemsSupport={!!onAssignSourceContentItem}
            />
          )}

          {/* Items list for selected chapter (items view) */}
          {viewLevel === 'items' && selectedChapter && (
            <SourceContentItemsList
              items={chapterItems}
              loading={loadingItems}
              addingItemId={addingItemId}
              onAddItem={handleAddItem}
              isItemAssigned={isItemAssigned}
              getItemTitle={getItemTitle}
              hasAssignSupport={!!onAssignSourceContentItem}
            />
          )}

          <p className="text-muted-foreground mt-3 text-xs">
            {viewLevel === 'chapters'
              ? 'Click the + button to assign a chapter, or click a row to view items.'
              : 'Click the + button to assign an item as homework.'}
          </p>
        </div>
      )}
    </div>
  )
}

// Breadcrumb navigation component
interface BreadcrumbProps {
  sourceName: string
  sourceIcon: string | null
  chapterTitle: string
  chapterType: string
  chapterOrder: number
  onBackToChapters: () => void
}

function Breadcrumb({
  sourceName,
  sourceIcon,
  chapterTitle,
  chapterType,
  chapterOrder,
  onBackToChapters,
}: BreadcrumbProps) {
  const typeLabel = CONTENT_TYPE_LABELS[chapterType] || chapterType
  const chapterLabel =
    chapterOrder > 0
      ? `${typeLabel} ${chapterOrder}: ${chapterTitle}`
      : chapterTitle

  return (
    <div className="bg-muted/30 mb-4 flex items-center gap-2 rounded-lg px-3 py-2 text-sm">
      <button
        type="button"
        onClick={onBackToChapters}
        className="text-primary hover:text-primary/80 flex items-center gap-1.5 font-medium transition-colors"
      >
        {sourceIcon && (
          <AuthoredIcon
            icon={sourceIcon}
            size="sm"
            className="text-muted-foreground shrink-0"
            fallback={Library}
          />
        )}
        {sourceName}
      </button>
      <ChevronRight size={14} className="text-muted-foreground" />
      <span className="text-foreground font-medium">{chapterLabel}</span>
    </div>
  )
}

interface SourceContentListProps {
  source: SourceWithContent
  assignedContentIds: Set<string>
  addingContentId: string | null
  onAddContent: (content: SourceContent) => void
  onDrillInto: (content: SourceContent) => void
  getContentLabel: (content: SourceContent) => string
  hasItemsSupport: boolean
}

function SourceContentList({
  source,
  assignedContentIds,
  addingContentId,
  onAddContent,
  onDrillInto,
  getContentLabel,
  hasItemsSupport,
}: SourceContentListProps) {
  if (source.contents.length === 0) {
    return (
      <div className="text-muted-foreground py-4 text-center text-sm">
        No content available for this source.
      </div>
    )
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      <table className="w-full">
        <thead className="bg-muted/30 sticky top-0">
          <tr className="text-muted-foreground text-left text-xs">
            <th className="w-16 px-3 py-2 font-medium">Action</th>
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Est. Time</th>
            {hasItemsSupport && <th className="w-10 px-3 py-2 font-medium" />}
          </tr>
        </thead>
        <tbody>
          {source.contents.map((content) => {
            const isAssigned = assignedContentIds.has(content.id)
            const isAdding = addingContentId === content.id
            const hasItems =
              content.itemCount !== undefined && content.itemCount > 0

            return (
              <tr
                key={content.id}
                className={`border-border border-b transition-colors ${
                  isAssigned ? 'bg-muted/20 opacity-60' : 'hover:bg-muted/30'
                } ${hasItemsSupport && hasItems ? 'cursor-pointer' : ''}`}
                {...activatableRowProps({
                  onActivate: () => onDrillInto(content),
                  label: `View items in ${content.title}`,
                  enabled: hasItemsSupport && hasItems,
                })}
              >
                <td className="px-3 py-2">
                  {isAssigned ? (
                    <div className="flex items-center gap-1 text-sm text-green-600">
                      <Check size={14} />
                      <span>Added</span>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        onAddContent(content)
                      }}
                      disabled={isAdding}
                      aria-label={`Add ${content.title} as homework`}
                    >
                      {isAdding ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Plus size={14} />
                      )}
                    </Button>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-foreground text-sm font-medium ${isAssigned ? 'line-through' : ''}`}
                    >
                      {content.title}
                    </span>
                    {content.url && (
                      <a
                        href={content.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Open ${content.title} in new tab`}
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {content.description && (
                    <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                      {content.description}
                    </p>
                  )}
                </td>
                <td className="text-muted-foreground px-3 py-2 text-sm">
                  <div className="flex items-center gap-1">
                    {getContentLabel(content)}
                    {hasItems && (
                      <span className="text-muted-foreground/60 text-xs">
                        ({content.itemCount} item
                        {content.itemCount !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {content.estimatedMinutes ? (
                    <div className="text-muted-foreground flex items-center gap-1 text-sm">
                      <Clock size={12} />
                      <span>{content.estimatedMinutes} min</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </td>
                {hasItemsSupport && (
                  <td className="px-3 py-2">
                    {hasItems && (
                      <ChevronRight
                        size={16}
                        className="text-muted-foreground"
                      />
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Items list component for displaying items within a chapter
interface SourceContentItemsListProps {
  items: SourceContentItem[]
  loading: boolean
  addingItemId: string | null
  onAddItem: (item: SourceContentItem) => void
  isItemAssigned: (item: SourceContentItem) => boolean
  getItemTitle: (item: SourceContentItem) => string
  hasAssignSupport: boolean
}

function SourceContentItemsList({
  items,
  loading,
  addingItemId,
  onAddItem,
  isItemAssigned,
  getItemTitle,
  hasAssignSupport,
}: SourceContentItemsListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="text-muted-foreground animate-spin" />
        <span className="text-muted-foreground ml-2 text-sm">
          Loading items...
        </span>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="text-muted-foreground py-4 text-center text-sm">
        No items in this chapter.
      </div>
    )
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      <table className="w-full">
        <thead className="bg-muted/30 sticky top-0">
          <tr className="text-muted-foreground text-left text-xs">
            {hasAssignSupport && (
              <th className="w-16 px-3 py-2 font-medium">Action</th>
            )}
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium">Type</th>
            <th className="px-3 py-2 font-medium">Duration</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isAssigned = isItemAssigned(item)
            const isAdding = addingItemId === item.id
            const title = getItemTitle(item)
            const typeLabel =
              ITEM_TYPE_LABELS[item.contentType] || item.contentType

            return (
              <tr
                key={item.id}
                className={`border-border border-b transition-colors ${
                  isAssigned ? 'bg-muted/20 opacity-60' : 'hover:bg-muted/30'
                }`}
              >
                {hasAssignSupport && (
                  <td className="px-3 py-2">
                    {isAssigned ? (
                      <div className="flex items-center gap-1 text-sm text-green-600">
                        <Check size={14} />
                        <span>Added</span>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => onAddItem(item)}
                        disabled={isAdding}
                        aria-label={`Add ${title} as homework`}
                      >
                        {isAdding ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Plus size={14} />
                        )}
                      </Button>
                    )}
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-foreground text-sm font-medium ${isAssigned ? 'line-through' : ''}`}
                    >
                      {title}
                    </span>
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Open ${title} in new tab`}
                      >
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                      {item.description}
                    </p>
                  )}
                </td>
                <td className="text-muted-foreground px-3 py-2 text-sm">
                  {typeLabel}
                </td>
                <td className="px-3 py-2">
                  {item.durationMinutes ? (
                    <div className="text-muted-foreground flex items-center gap-1 text-sm">
                      <Clock size={12} />
                      <span>{item.durationMinutes} min</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
